import { Injectable } from '@angular/core';
import { EventsService } from '../event/event.service';
import { UserSessionService } from '../user-session/user-session.service';
import { AuthenticationService } from '../authentication/authentication.service';
import { ConfigService } from '../config/config.service';
import { MenuStateService } from '../menu-state/menu-state.service';
import { Platform } from '@ionic/angular';
import { RoutingService } from '../routing/routing.service';
import { Navigator } from '../routing/navigator';
import { GeoLocationStrategyService } from '../geo-location-strategy/geo-location-strategy.service';
import { GeoLocationControllerService } from '../geo-location/geo-location-controller.service';

@Injectable({
	providedIn: 'root'
})
export class LogoutService {

	public responsibleGamingNumber: string;
	public responsibleGamingMessage: string;
	public isShowingPopover: boolean = false;
	public calledWithinInterval = false;
	public isAuthenticated: boolean;

	constructor(
		private authenticationService: AuthenticationService,
		private config: ConfigService,
		private eventsService: EventsService,
		private menuStateService: MenuStateService,
		private navigator: Navigator,
		private platform: Platform,
		private routing: RoutingService,
		private geoLocationControllerService: GeoLocationControllerService,
		private geoLocationStrategyService: GeoLocationStrategyService,
		private userSession: UserSessionService,
	) {
		this.responsibleGamingNumber = this.config.getConfig()?.RESPONSIBLE_GAMING_NUMBER;
		this.responsibleGamingMessage = this.config.getConfig().RESPONSIBLE_GAMING_MESSAGE || this.config.getConfig().RESPONSIBLE_GAMING_TEXT ||
			`If you or someone you know has a gambling problem, crisis counselling and referral services can be accessed by calling ${this.responsibleGamingNumber}.`;

		this.eventsService.subscribe('GlobalErrorHandlerService:action:logout', (params) => {
			if (this.isAuthenticated) {
				this.onLogout(params);
			}
		});

		//noticed, sometimes 'LOGOUT' doesn't call properly and EventSource gets closed too fast in notification-api.service.ts
		//adding below extra logout call to make sure logout happens for same device/different device with same user login.
		this.eventsService.subscribe('logout:refresh', () => {
			this.onLogout();
		})
	}

	onLogout(params: any = null) {
	    if (this.geoLocationStrategyService.isGeoOptimizationEnabled()) this.geoLocationControllerService.cleanupGeoLocation();
		this.callLogout(params);
		this.eventsService.unsubscribe('GlobalErrorHandlerService:action:logout');
		this.eventsService.publish('event:authentication:logout');
		this.eventsService.publish("event:lobby:show");
		this.eventsService.publish("event:notification:resume");
	}

	callLogout(params: any = null) {
		this.authenticationService.callLogout(params).subscribe(
			(response: any) => { },
		);
		this.eventsService.publish("event:close:IAB"); // to close IAB 
		this.callClientCleanup();
		this.checkLogoutCode(params);
	}

	callClientCleanup() {
		//Remove any active modals only if user gets logged out
		//specific case like same login user logged in other device or window
		if (this.navigator.activeModals.length > 0) {
			this.navigator.activeModals.forEach(modal => {
				modal.dismiss();
			});
			this.navigator.activeModals = [];
		}

		// Remove any active popover only if user gets logged out
		if (!this.isShowingPopover && this.navigator.activePopovers.length > 0) {
			this.navigator.activePopovers.forEach(popover => {
				popover.dismiss();
			});
			this.navigator.activePopovers = [];
		}
		// Clear User Session
		this.userSession.clear();

		// Close Sidemenu
		this.menuStateService.closeMenu('main-menu');
		this.menuStateService.closeMenu('secondary-menu');

		// For things like Lobby and keyword Updates
		this.eventsService.publish('event:authentication:logout');
	}

	showMessage(message = 'You are now logged out.') {
		if (this.isShowingPopover) {
			console.log("already showed the log out dialog");
		} else {
			this.isShowingPopover = !this.isShowingPopover;
			let popoverPromise = this.navigator.openPopover('PopoverContent', { message: message }, 'standard-popover message-align-left', false, true, true, 15000);
			popoverPromise.then((popover) => {
				popover.onDidDismiss().then(() => {
					this.isShowingPopover = false; //reset the varibale on dismiss instead callLogout call
					this.navigator.dismissAll();
					if (this.platform.is('ios') && !this.platform.is('capacitor')) {
						this.routing.defaultRoute();
					}
					this.onAppReload();
				})
			})
		}
	}

	showLoginDialog() {
		if (!this.calledWithinInterval) {
			this.userSession.clear();
			this.eventsService.publish("event:authentication:logout");
			this.navigator.navigateTo("LoginComponent");
		}

		this.calledWithinInterval = true;

		setTimeout(() => {
			this.calledWithinInterval = false;
		}, 2000);
	}

	checkLogoutCode(params) {

		if (params && 'code' in params) {
			switch (params.code) {
				case 'INVALID_SESSION_TOKEN':
					this.showMessage(
						`You are now logged out.<br /><br /> ${this.responsibleGamingMessage}`
					);
					break;
				case 'RUNNING_MULTIPLE_TABS':
					this.showMessage(
						`Your connection was severed. <br /><br />You are now logged out.<br /><br /> ${this.responsibleGamingMessage}`
					);
					break;
				case 'INACTIVITY':
					this.showMessage(
						`You are now logged out due to inactivity.<br /><br /> ${this.responsibleGamingMessage}`
					);
					break;
				case 'INVALID_DISCONNECT_TIME_ALLOWED':
					this.showLoginDialog();
					break;
				case 'QUALIFICATION_KBA_FAIL':
				case 'QUALIFICATION_ID_SCAN_FAIL':
					this.showMessage(
						`ID Verification failed.<br /><br />Please contact customer support.`
					);
					break;
				default:
					this.showMessage(
						`You are now logged out.<br /><br /> ${this.responsibleGamingMessage}`
					);
					break;
			}
		} else {
			this.showMessage(`You are now logged out.<br /><br />${this.responsibleGamingMessage}`);
		}
	}

	public onAppReload() {
		const origin: string = window.location.origin;
		window.location.href = origin;
		return setTimeout(() => window.location.reload(), 100);
	}

}
